// ============================================================
// TaskFlow OS - Animated Edge (SVG)
// Central line + spiral helices + flowing animation
// ============================================================

import React, { memo } from 'react';
import { BaseEdge, getStraightPath, getBezierPath, type EdgeProps } from '@xyflow/react';

interface AnimatedEdgeProps extends EdgeProps {
  data?: {
    isActive?: boolean;
    label?: string;
  };
}

const AnimatedEdge: React.FC<AnimatedEdgeProps> = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  style = {},
  markerEnd,
}) => {
  const isActive = data?.isActive !== false;

  // Get bezier path
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  // Calculate path length and direction for spiral generation
  const dx = targetX - sourceX;
  const dy = targetY - sourceY;
  const length = Math.sqrt(dx * dx + dy * dy);
  const steps = Math.max(20, Math.floor(length / 8));
  const amplitude = 6;
  const frequency = length / 30;

  // Generate spiral helix paths
  const generateSpiralPath = (offset: number): string => {
    const points: string[] = [];
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      // Bezier interpolation
      const cx1 = sourceX + dx * 0.25;
      const cy1 = sourceY;
      const cx2 = targetX - dx * 0.25;
      const cy2 = targetY;
      const mt = 1 - t;
      const baseX = mt * mt * mt * sourceX + 3 * mt * mt * t * cx1 + 3 * mt * t * t * cx2 + t * t * t * targetX;
      const baseY = mt * mt * mt * sourceY + 3 * mt * mt * t * cy1 + 3 * mt * t * t * cy2 + t * t * t * targetY;

      // Perpendicular offset for spiral
      const angle = Math.atan2(dy, dx) + Math.PI / 2;
      const spiralOffset = Math.sin(t * frequency * Math.PI * 2 + offset) * amplitude * Math.sin(t * Math.PI);
      const px = baseX + Math.cos(angle) * spiralOffset;
      const py = baseY + Math.sin(angle) * spiralOffset;

      points.push(i === 0 ? `M ${px} ${py}` : `L ${px} ${py}`);
    }
    return points.join(' ');
  };

  const spiral1 = generateSpiralPath(0);
  const spiral2 = generateSpiralPath(Math.PI);

  const activeColor = '#10b981';
  const inactiveColor = 'rgba(255, 255, 255, 0.15)';
  const mainColor = isActive ? activeColor : inactiveColor;

  return (
    <g className="react-flow__edge-animated-custom">
      <defs>
        <linearGradient id={`edge-gradient-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#10b981" stopOpacity={isActive ? 0.8 : 0.2} />
          <stop offset="50%" stopColor="#3b82f6" stopOpacity={isActive ? 0.8 : 0.2} />
          <stop offset="100%" stopColor="#06b6d4" stopOpacity={isActive ? 0.8 : 0.2} />
        </linearGradient>

        {isActive && (
          <filter id={`glow-${id}`}>
            <feGaussianBlur stdDeviation="2" result="blur" />
            <feComposite in="SourceGraphic" in2="blur" operator="over" />
          </filter>
        )}
      </defs>

      {/* Background glow (active only) */}
      {isActive && (
        <path
          d={edgePath}
          fill="none"
          stroke={mainColor}
          strokeWidth={8}
          strokeOpacity={0.08}
          filter={`url(#glow-${id})`}
        />
      )}

      {/* Central main line */}
      <path
        d={edgePath}
        fill="none"
        stroke={`url(#edge-gradient-${id})`}
        strokeWidth={isActive ? 2 : 1}
        style={{
          strokeDasharray: isActive ? '8 4' : 'none',
          animation: isActive ? 'flow-edge 1.5s linear infinite' : 'none',
        }}
      />

      {/* Spiral helix 1 */}
      <path
        d={spiral1}
        fill="none"
        stroke={mainColor}
        strokeWidth={0.8}
        strokeOpacity={isActive ? 0.4 : 0.1}
        style={{
          strokeDasharray: isActive ? '4 6' : 'none',
          animation: isActive ? 'spiral-flow 2s linear infinite' : 'none',
        }}
      />

      {/* Spiral helix 2 */}
      <path
        d={spiral2}
        fill="none"
        stroke={isActive ? '#3b82f6' : inactiveColor}
        strokeWidth={0.8}
        strokeOpacity={isActive ? 0.35 : 0.1}
        style={{
          strokeDasharray: isActive ? '4 6' : 'none',
          animation: isActive ? 'spiral-flow 2s linear infinite reverse' : 'none',
        }}
      />

      {/* Flowing particles (active only) */}
      {isActive && (
        <>
          <circle r="3" fill={activeColor} fillOpacity="0.6">
            <animateMotion dur="2s" repeatCount="indefinite" path={edgePath} />
          </circle>
          <circle r="2" fill="#3b82f6" fillOpacity="0.5">
            <animateMotion dur="2.5s" repeatCount="indefinite" path={edgePath} begin="0.5s" />
          </circle>
          <circle r="1.5" fill="#06b6d4" fillOpacity="0.4">
            <animateMotion dur="3s" repeatCount="indefinite" path={edgePath} begin="1s" />
          </circle>
        </>
      )}

      {/* Label */}
      {data?.label && (
        <g transform={`translate(${labelX}, ${labelY})`}>
          <rect
            x={-data.label.length * 3.5 - 8}
            y={-12}
            width={data.label.length * 7 + 16}
            height={24}
            rx={6}
            fill="rgba(0, 0, 0, 0.7)"
            stroke="rgba(255, 255, 255, 0.1)"
            strokeWidth={1}
          />
          <text
            textAnchor="middle"
            dominantBaseline="central"
            className="text-[10px] fill-white/70"
          >
            {data.label}
          </text>
        </g>
      )}
    </g>
  );
};

export default memo(AnimatedEdge);
